// src/components/layout/AppLayout.js
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import styles from './AppLayout.module.css';

import {
    LayoutDashboard, Package, Boxes, Warehouse, Building2,
    ShieldCheck, Bell, BrainCircuit, BookOpen, Megaphone,
    LogOut, Menu, X, ChevronRight, Wifi, Users, Clock,
    CreditCard, DollarSign, AlertTriangle,
} from 'lucide-react';

const NAV_ITEMS = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/inventory', icon: Boxes, label: 'Inventory' },
    { to: '/products', icon: Package, label: 'Products' },
    { to: '/warehouses', icon: Warehouse, label: 'Warehouses' },
    { to: '/certificates', icon: Wifi, label: 'Readers' },
    { to: '/alerts', icon: Bell, label: 'Alerts' },
    { to: '/ai-chat', icon: BrainCircuit, label: 'GAAF AI Assistant' },
    { to: '/knowledge-base', icon: BookOpen, label: 'Knowledge Base' },
    { to: '/staff', icon: Users, label: 'Staff' },
    { to: '/attendance', icon: Clock, label: 'Attendance' },
    { to: '/billing', icon: CreditCard, label: 'Billing' },
];

const ADMIN_ITEMS = [
    { to: '/companies', icon: Building2, label: 'Companies' },
    { to: '/billing/admin', icon: DollarSign, label: 'Billing Overview' },
    { to: '/ads', icon: Megaphone, label: 'Ad Manager' },
];

export default function AppLayout() {
    const { user, logout, isGaafAdmin, isSubscriptionBlocked, subscription } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    // Redirect blocked users to billing — allow /billing routes through
    useEffect(() => {
        if (isSubscriptionBlocked && !location.pathname.startsWith('/billing')) {
            navigate('/billing', { replace: true });
        }
    }, [isSubscriptionBlocked, location.pathname]);

    const handleLogout = () => { logout(); navigate('/login'); };

    const NavItem = ({ item }) => (
        <NavLink
            to={item.to}
            end={item.end}
            className={({ isActive }) => `${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
            onClick={() => setMobileOpen(false)}
        >
            <item.icon size={18} className={styles.navIcon} />
            {!collapsed && <span className={styles.navLabel}>{item.label}</span>}
            {!collapsed && <ChevronRight size={14} className={styles.navChevron} />}
        </NavLink>
    );

    const sidebar = (
        <aside className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`}>
            <div className={styles.logo}>
                <div className={styles.logoMark}><span className={styles.logoG}>G</span></div>
                {!collapsed && (
                    <div className={styles.logoText}>
                        <span className={styles.logoName}>GAAF<em>rfid</em></span>
                        <span className={styles.logoTag}>Inventory Platform</span>
                    </div>
                )}
            </div>

            {!collapsed && user?.company && (
                <div className={styles.companyPill}>
                    <div className={styles.companyDot} />
                    <span className={styles.companyName}>{user.company.name}</span>
                </div>
            )}

            <nav className={styles.nav}>
                <div className={styles.navGroup}>
                    {!collapsed && <span className={styles.navGroupLabel}>Main</span>}
                    {/* Hide /billing from GAAF admins — they use /billing/admin */}
                    {NAV_ITEMS.filter(i => !(isGaafAdmin && i.to === '/billing')).map(item => (
                        <NavItem key={item.to} item={item} />
                    ))}
                </div>

                {isGaafAdmin && (
                    <div className={styles.navGroup}>
                        {!collapsed && <span className={styles.navGroupLabel}>Admin</span>}
                        {ADMIN_ITEMS.map(item => <NavItem key={item.to} item={item} />)}
                    </div>
                )}
            </nav>

            <div className={styles.sidebarFooter}>
                {!collapsed && (
                    <div className={styles.userInfo}>
                        <div className={styles.userAvatar}>
                            {(user?.name || user?.email || '?')[0].toUpperCase()}
                        </div>
                        <div className={styles.userDetails}>
                            <span className={styles.userName}>{user?.name || user?.email}</span>
                            <span className={styles.userRole}>{user?.role?.replace('_', ' ')}</span>
                        </div>
                    </div>
                )}
                <button className={styles.logoutBtn} onClick={handleLogout} title="Sign Out">
                    <LogOut size={16} />
                </button>
            </div>
        </aside>
    );

    return (
        <div className={styles.layout}>
            {mobileOpen && <div className={styles.mobileOverlay} onClick={() => setMobileOpen(false)} />}
            <div className={`${styles.sidebarWrap} ${mobileOpen ? styles.sidebarMobileOpen : ''}`}>
                {sidebar}
                <button className={styles.collapseBtn} onClick={() => setCollapsed(!collapsed)}
                    title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                    {collapsed ? <ChevronRight size={14} /> : <X size={14} />}
                </button>
            </div>
            <div className={`${styles.main} ${collapsed ? styles.mainExpanded : ''}`}>
                <header className={styles.topbar}>
                    <button className={styles.mobileMenuBtn} onClick={() => setMobileOpen(!mobileOpen)}>
                        <Menu size={20} />
                    </button>
                    <div className={styles.topbarRight}>
                        <div className={styles.onlineDot} title="System online" />
                    </div>
                </header>
                <main className={styles.content}>
                    {isSubscriptionBlocked && (
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            background: 'rgba(201,80,80,0.1)', border: '1px solid rgba(201,80,80,0.25)',
                            borderRadius: 'var(--radius-lg)', padding: '12px 16px',
                            marginBottom: 20, color: '#e07a7a', fontSize: '0.875rem',
                        }}>
                            <AlertTriangle size={16} style={{ flexShrink: 0 }} />
                            <span>
                                <strong>Account Suspended.</strong> Your subscription is {subscription?.status || 'inactive'}.
                                {' '}Please update your payment to restore Full Access.
                            </span>
                        </div>
                    )}
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
